/**
 * The scans we host ourselves, which way up each one is, and where each came from.
 *
 * Hosting these rather than embedding a third-party viewer is what makes the
 * orientation fixable. The SuperSplat viewer rotates every splat 180 degrees about Z on
 * load, because the PLY convention puts +Y into the ground; our exports are already
 * Y-up, so that rotation is what puts them on their heads. It also explains why the same
 * file looks right in the SuperSplat editor and wrong in its viewer — the editor does not
 * apply the rotation.
 *
 * Here the rotation is a value, and it defaults to none. `scripts/splat_orientation.py`
 * reads a .sog and reports which convention it uses, so `rotateZ` is set from what the
 * file actually contains rather than from a guess or a filename.
 *
 * `origin` is the other field that has to be right. A splat reconstructed from
 * photographs is not a scan of a site, and describing one as the other is a claim about
 * how the work was done. The Greek rooms were generated from Emmett Brogan's pictures
 * and say so.
 *
 * The Saudi rooms are `own`: the studio's own material, confirmed as ours to use. That
 * settles who they belong to and deliberately does not settle how they were made, which
 * nobody has stated and which this field would otherwise be inventing. So the cards
 * carry the studio's name and no verb — not "captured", not "generated from" — because
 * the one fact we have is ownership and the page may only claim what it has.
 */

import type { RegionId } from "../regions";

/**
 * How the room came to exist. Decides what the page may claim about it.
 *
 * `generated` names a reconstruction from somebody's photographs and carries their
 * credit. `own` is ours, with no claim either way about capture or generation.
 */
export type SceneOrigin = "generated" | "own";

export type SplatScene = {
  slug: string;
  /** File under `public/splats/`. Never a URL from the page's own query string. */
  file: string;
  title: string;
  /**
   * Degrees about Z. 0 for a Y-up export, 180 for one following the PLY convention.
   * Measured with `scripts/splat_orientation.py`, not assumed.
   */
  rotateZ: 0 | 180;
  region: RegionId;
  origin: SceneOrigin;
  /**
   * A still from `public/collection/` to hold the frame while the splat loads.
   *
   * A .sog is twenty-odd megabytes and takes several seconds to sort and upload after
   * the asset itself has arrived, and the viewer used to spend that time as a black
   * rectangle. For the Greek rooms there is an obvious still to use: the photograph the
   * room was generated from. The Saudi rooms have no such photograph, so their stills
   * are frames of the rooms themselves, taken by `web/scripts/capture-room-posters.mjs`
   * — the honest holding image for a room being a picture of that room.
   */
  poster?: string;
};

export const SPLAT_SCENES: readonly SplatScene[] = [
  {
    slug: "diriyah-fort",
    file: "Ancient_Mud-Brick_Fort_of_Diriyah.sog",
    title: "The mud-brick fort at Diriyah",
    rotateZ: 0,
    region: "saudi-arabia",
    origin: "own",
    poster: "room-diriyah-fort.jpg",
  },
  {
    slug: "rijal-almaa",
    file: "Rijal_Almaa_Heritage_Village.sog",
    title: "Rijal Almaa heritage village",
    rotateZ: 0,
    region: "saudi-arabia",
    origin: "own",
    poster: "room-rijal-almaa.jpg",
  },
  {
    slug: "maraya",
    file: "Desert_Sunset_at_Maraya.sog",
    title: "Maraya at sunset",
    rotateZ: 0,
    region: "saudi-arabia",
    origin: "own",
    poster: "room-maraya.jpg",
  },
  // Ancient Gold Coin Display was here and is not any more, for a reason none of the
  // others have: it reconstructs as an object seen from outside rather than a place seen
  // from within. Its authored viewpoint sits beyond the walls, so opening at that point
  // — which is what both the splat page and `scan.ts` do — looks in at a lit dollhouse
  // floating in black. The registry's rule is that a room is somewhere you can stand,
  // and there is nowhere in that file to stand. The .sog is still on disk; if a way to
  // name a stand-point per scene is ever wanted, it comes back in one entry.
  //
  // Six more scans in the same library are deliberately not here, and the reasons
  // are worth keeping because they are the same three tests everything else on this
  // site is held to. Jeddah Coastal Cityscape and Glowing Circular City Oasis and
  // Futuristic Desert Plaza at Dusk are speculative future cities, not heritage, and
  // a page that mixes them in is quietly claiming a country's past looks like its
  // renderings of its future. Riyadh Sports Boulevard Construction is a building
  // site. Panoramic View of Hanauma Bay is in Hawaii — the same mistake as the
  // Mughal tomb below, from the same library, and caught the same way: by reading
  // the title rather than the thumbnail.
  //
  // The octagonal stone tomb that used to sit here is gone. It is a Mughal monument —
  // Lahore, not the Arabian peninsula — and a heritage page that puts one country's
  // architecture under another country's name is making a false claim whatever else on
  // the page is careful. Hegra and AlUla below are the material it should have been.
  {
    slug: "hegra-tombs",
    file: "Ancient_Tombs_of_Hegra.sog",
    title: "The tombs at Hegra",
    rotateZ: 0,
    region: "saudi-arabia",
    origin: "own",
    poster: "room-hegra-tombs.jpg",
  },
  {
    slug: "hegra-solitary-tomb",
    file: "The_Solitary_Tomb_of_Hegra.sog",
    title: "Qasr al-Farid, the lone tomb at Hegra",
    rotateZ: 0,
    region: "saudi-arabia",
    origin: "own",
    poster: "room-hegra-solitary-tomb.jpg",
  },
  {
    slug: "alula-lion-tombs",
    file: "Lion_Tombs_of_Al-Ula.sog",
    title: "The lion tombs at AlUla",
    rotateZ: 0,
    region: "saudi-arabia",
    origin: "own",
    poster: "room-alula-lion-tombs.jpg",
  },
  {
    // Named for what is in the frame and nothing more. The file says desert ruins and a
    // palm oasis, and nobody has told us which oasis — so the card does not name one.
    slug: "desert-oasis-ruins",
    file: "Desert_Ruins_and_Palm_Oasis.sog",
    title: "Ruins above a palm oasis",
    rotateZ: 0,
    region: "saudi-arabia",
    origin: "own",
    poster: "room-desert-oasis-ruins.jpg",
  },
  {
    slug: "alula-oasis-fortress",
    file: "Ancient_Mud-Brick_Oasis_Fortress.sog",
    title: "A mud-brick fortress above an oasis",
    rotateZ: 0,
    region: "saudi-arabia",
    origin: "own",
    poster: "room-alula-oasis-fortress.jpg",
  },
  {
    slug: "mirrors-desert-canyon",
    file: "Riyadh_Mirrors_in_the_Desert_Canyon.sog",
    title: "Mirrors in a desert canyon",
    rotateZ: 0,
    region: "saudi-arabia",
    origin: "own",
    poster: "room-mirrors-desert-canyon.jpg",
  },
  {
    slug: "heritage-museum-courtyard",
    file: "Traditional_Heritage_Museum_Courtyard.sog",
    title: "A heritage museum courtyard",
    // 180, against the script's reading of it. This is exactly the case
    // `splat_orientation.py` says it cannot judge: an interior has no sky, so the
    // largest gaussians are a floor or a ceiling and whichever wins is arbitrary. It
    // reported "clear" on a 14% margin — the weakest of the twelve — and was wrong.
    // Looked at, the scene is plainly on its head.
    rotateZ: 180,
    region: "saudi-arabia",
    origin: "own",
    poster: "room-heritage-museum-courtyard.jpg",
  },
  {
    slug: "cultural-centre-plaza",
    file: "Grand_Cultural_Center_Plaza.sog",
    title: "The plaza of a cultural centre",
    rotateZ: 0,
    region: "saudi-arabia",
    origin: "own",
    poster: "room-cultural-centre-plaza.jpg",
  },
  {
    slug: "athens-parthenon",
    file: "The_Parthenon_on_the_Acropolis.sog",
    title: "The Parthenon, on the Acropolis",
    rotateZ: 0,
    region: "greece",
    origin: "generated",
    poster: "parthenon.webp",
  },
  {
    slug: "athens-odeon",
    file: "Odeon_of_Herodes_Atticus.sog",
    title: "The Odeon of Herodes Atticus",
    rotateZ: 0,
    region: "greece",
    origin: "generated",
    poster: "odeon-of-herodes-atticus.webp",
  },
  {
    slug: "athens-national-garden",
    file: "Sundial_in_the_National_Garden.sog",
    title: "The sundial in the National Garden",
    rotateZ: 0,
    region: "greece",
    origin: "generated",
    poster: "national-garden-sundial.webp",
  },
  {
    slug: "athens-moonlight",
    file: "Moonlit_Night_Over_Athens.sog",
    title: "Athens by moonlight",
    rotateZ: 0,
    region: "greece",
    origin: "generated",
    poster: "athens-moonrise.webp",
  },
  {
    slug: "thessaloniki-white-tower",
    file: "The_White_Tower_Waterfront_Scene.sog",
    title: "The White Tower, on the waterfront",
    rotateZ: 0,
    region: "greece",
    origin: "generated",
    poster: "white-tower.webp",
  },
];

/**
 * Resolve a slug to a scene.
 *
 * A slug, never a path or a URL: this page is opened by a query string, and taking a
 * filename from one would let any address on the internet be loaded and rendered under
 * the museum's own origin.
 */
export function sceneFor(slug: string | null): SplatScene | null {
  return SPLAT_SCENES.find((scene) => scene.slug === slug) ?? null;
}

export function scenesIn(region: RegionId): readonly SplatScene[] {
  return SPLAT_SCENES.filter((scene) => scene.region === region);
}
